/**
 * Smidly typography scale. Each role maps to a global `.text-<role>` class defined in
 * `client/index.css` (`@layer components`), driven by `--text-<role>-size|weight|leading|tracking`
 * variables that chain from a few weight tiers (`--font-weight-body|menu|action|heading|label|strong`).
 * To restyle text, edit those variables, not this file. Sizes match the `--font-size-*` tokens.
 * Rules: `docs/design-system/tokens.md`.
 * Use via the `Text` component or `getTextStyleClasses()`.
 *
 * Only non-typographic extras live here (`tabular-nums` so figures align down a column,
 * `uppercase` for overline-style roles). Raw `font-*` / `text-<size>` utilities outside
 * `components/ui` are flagged by ESLint (warning).
 */
export const TEXT_STYLES = {
  'display.l': 'text-display-l',
  'display.m': 'text-display-m',
  /** KPI figure. Viewport clamp stays below heading-display sizes so a 1-row tile fits title, value and comparison. */
  'display.kpi': 'text-display-kpi tabular-nums',
  /** Marketing scale for public-page hero headlines (30 / 36 / 48 / 60 / 72px) and lead paragraphs (18 / 20px). */
  'display.hero.s': 'text-display-hero-s',
  'display.hero.m': 'text-display-hero-m',
  'display.hero.l': 'text-display-hero-l',
  'display.hero.xl': 'text-display-hero-xl',
  'display.hero.xxl': 'text-display-hero-xxl',
  'display.lead': 'text-display-lead',
  'display.lead.l': 'text-display-lead-l',
  'heading.xl': 'text-heading-xl',
  'heading.l': 'text-heading-l',
  'heading.m': 'text-heading-m',
  'heading.s': 'text-heading-s',
  'body.l': 'text-body-l',
  'body.m': 'text-body-m',
  'body.s': 'text-body-s',
  'label.l': 'text-label-l',
  'label.m': 'text-label-m',
  'label.s': 'text-label-s',
  'menu': 'text-menu',
  'menu.group': 'text-menu-group uppercase',
  'table.header': 'text-table-header tabular-nums',
  'table.cell': 'text-table-cell tabular-nums',
  'table.cell.emphasis': 'text-table-cell-emphasis tabular-nums',
  'caption': 'text-caption',
  'overline': 'text-overline uppercase',
  'button': 'text-button',
  'button.sm': 'text-button-sm',
} as const;

export type TextStyle = keyof typeof TEXT_STYLES;

/** `display-l`, `body-m`, ...: the part after `text-` of every role class (used to teach tailwind-merge). */
export const TEXT_ROLE_CLASS_SUFFIXES: string[] = Object.keys(TEXT_STYLES).map((role) => role.replace(/\./g, '-'));

/** Styles that use muted foreground when `colour` is enabled. */
const MUTED_TEXT_STYLES = new Set<TextStyle>([
  'label.m',
  'label.s',
  'caption',
  'overline',
  'table.header',
  'menu.group',
]);

export function getTextStyleClasses(textStyle: TextStyle, colour = true): string {
  const base = TEXT_STYLES[textStyle];
  if (!colour) return base;
  return MUTED_TEXT_STYLES.has(textStyle)
    ? `${base} text-muted-foreground`
    : `${base} text-foreground`;
}
